import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const CATEGORIES = [
  "Books & Learning",
  "Home & Environment",
  "Everyday Wellness",
  "Personal Care",
  "Tools & Resources",
];

const EMPTY = {
  name: "",
  brand: "",
  description: "",
  category: CATEGORIES[0],
  note: "",
  url: "",
  image: "",
  featured: false,
  order: 0,
  published: true,
};

const inputCls =
  "w-full rounded-2xl border border-forest/20 bg-white/70 px-4 py-2.5 text-sm outline-none focus:border-gold";

const Editor = ({ initial, categories, onSaved, onCancel }) => {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/resources/upload", fd);
      setF((v) => ({ ...v, image: data.url }));
      toast.success("Image uploaded.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Upload failed."));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, order: Number(f.order) || 0 };
      if (f.fav_id) {
        await api.put(`/admin/resources/favorites/${f.fav_id}`, payload);
      } else {
        await api.post("/admin/resources/favorites", payload);
      }
      toast.success("Saved.");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div data-testid="favorite-editor" className="mt-4 flex flex-col gap-3 rounded-2xl border border-gold/30 bg-sage/40 p-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input data-testid="fav-name" placeholder="Product or resource name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
        <input data-testid="fav-brand" placeholder="Brand or merchant" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} className={inputCls} />
      </div>
      <input data-testid="fav-url" placeholder="Full affiliate link, exactly as issued (https://...?tag=...)" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} className={inputCls} />
      <p className="text-[11px] text-taupe">
        Paste the complete link from your affiliate program. Tracking parameters are stored and
        used exactly as entered, never shortened or altered.
      </p>
      <textarea data-testid="fav-description" rows={2} placeholder="Short description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} />
      <textarea data-testid="fav-note" rows={2} placeholder="Your personal note (optional): why you love it" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={inputCls} />
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="fav-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={`${inputCls} w-auto rounded-full`}>
          {[...new Set([...CATEGORIES, ...categories])].map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <button data-testid="fav-image-upload" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-4 py-2 text-xs font-semibold text-forest hover:border-gold">
          <Upload className="h-3.5 w-3.5" />
          {f.image ? "Replace image" : "Upload image"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        {f.image && <img src={f.image} alt="Product preview" className="h-10 rounded-lg" />}
      </div>
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" data-testid="fav-featured" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} className="brand-check h-4 w-4" />
          Featured
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" data-testid="fav-published" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} className="brand-check h-4 w-4" />
          Published
        </label>
        <label className="flex items-center gap-2 text-xs text-taupe">
          Display order
          <input data-testid="fav-order" type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} className="w-20 rounded-full border border-forest/20 bg-white/70 px-3 py-1.5 text-xs outline-none focus:border-gold" />
        </label>
      </div>
      <div className="flex gap-3">
        <button data-testid="fav-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-xs font-semibold text-ivory hover:bg-forest-deep disabled:opacity-50">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save
        </button>
        <button onClick={onCancel} className="text-xs font-semibold text-taupe underline-offset-4 hover:underline">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default function AdminFavorites() {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/resources/favorites").then(({ data }) => setData(data));

  useEffect(() => {
    document.title = "Affiliate Resources | Rooted Healing Admin";
    load().catch(() => setData({ items: [], clicks: {} }));
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this recommendation from the website?")) return;
    await api.delete(`/admin/resources/favorites/${id}`);
    toast.success("Removed.");
    load();
  };

  if (!data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-forest" />
      </div>
    );
  }

  const customCategories = [...new Set(data.items.map((i) => i.category).filter(Boolean))];

  return (
    <div data-testid="admin-favorites">
      <h1 className="font-serif text-3xl text-forest">Affiliate Resources</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-taupe">
        Curate the products and resources on Staci's Favorites. Outbound clicks are counted for
        your reference; clicks are never treated as purchases or commissions.
      </p>

      <button
        data-testid="fav-add"
        onClick={() => {
          setAdding(true);
          setEditing(null);
        }}
        className="mt-6 rounded-full border border-gold/50 px-5 py-2.5 text-sm font-semibold text-gold-dark hover:bg-gold/10"
      >
        + Add a recommendation
      </button>

      {adding && (
        <Editor
          initial={{ ...EMPTY }}
          categories={customCategories}
          onSaved={() => {
            setAdding(false);
            load();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <div className="mt-6 flex flex-col gap-3">
        {data.items.length === 0 && !adding && (
          <p className="rounded-2xl border border-forest/10 bg-white/75 p-8 text-center text-sm text-taupe">
            No recommendations yet. Add your first favorite above.
          </p>
        )}
        {data.items.map((item) => (
          <div key={item.fav_id} data-testid={`fav-row-${item.fav_id}`} className="rounded-2xl border border-forest/10 bg-white/75 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                {item.image && <img src={item.image} alt="" className="h-12 w-12 rounded-xl object-cover" />}
                <div className="min-w-0">
                  <p className="truncate font-semibold text-forest">
                    {item.name}
                    {item.brand && <span className="ml-2 text-xs font-normal text-taupe">{item.brand}</span>}
                  </p>
                  <p className="mt-0.5 text-xs text-taupe">
                    {item.category} · {data.clicks[item.fav_id] || 0} outbound clicks
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${item.published ? "bg-sage text-forest" : "bg-charcoal/10 text-taupe"}`}>
                  {item.published ? "published" : "hidden"}
                </span>
                {item.featured && (
                  <span className="rounded-full bg-gold/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-gold-dark">
                    featured
                  </span>
                )}
                <button
                  data-testid={`fav-edit-${item.fav_id}`}
                  onClick={() => setEditing(editing === item.fav_id ? null : item.fav_id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-forest/20 px-4 py-2 text-xs font-semibold text-forest hover:border-gold"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  data-testid={`fav-delete-${item.fav_id}`}
                  onClick={() => remove(item.fav_id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-red-900/20 px-4 py-2 text-xs font-semibold text-red-900/70 hover:border-red-900/50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            {editing === item.fav_id && (
              <Editor
                initial={{ ...EMPTY, ...item }}
                categories={customCategories}
                onSaved={() => {
                  setEditing(null);
                  load();
                }}
                onCancel={() => setEditing(null)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
